import { UserServiceClient } from 'fitbeat.api-clients';
import { BookingServiceClient } from 'fitbeat.api-clients';
import { musicPlatform } from 'fitbeat.gym-core/config';
import { CardioOrResistance } from 'fitbeat.models';
import { Platform } from 'react-native';
import { getApiConfig, getConfig } from '../../../config';
import { analyicsForAction } from '../../../services/google-analytics/analyicsForAction';
import { AuthTokenBuilder } from '../../../utils/authTokenBuilder';
import { Logger } from '../../../utils/Logger';
import Storage from '../../../utils/Storage';
import { closeSetupScreen, IOpenHomeWorkoutRequest, openSetupScreen } from '../../actions';
import { IAppStore } from '../../reducer';
import { DeviceSetupScreens } from '../../utils/DeviceSetupScreens';
import { AppleMusicSource } from './AppleMusicSource';
import { IMusicPlayerSource, IPlayerState, IPlaylist, ISong } from './interfaces';
import { SpotifyMusicSource } from './SpotifyMusicSource';
import * as EliteAppManagement from '../../../elite/appManagement';
import { openLink } from '../../../utils/linkUtils';

export const MUSIC_AUTOPLAY_INITIALISING = 'MUSIC_AUTOPLAY_INITIALISING';
export const MUSIC_AUTOPLAY_SETUP = 'MUSIC_AUTOPLAY_SETUP';
export const MUSIC_AUTOPLAY_INITIALISED = 'MUSIC_AUTOPLAY_INITIALISED';
export const PLAYER_STATE_CHANGE = 'PLAYER_STATE_CHANGE';
export const SET_MUSIC_PLAYER_TO_AUTOPLAY = 'SET_MUSIC_PLAYER_TO_AUTOPLAY';
export const CONNECTION_STATE_CHANGE = 'CONNECTION_STATE_CHANGE';
export const SELECTED_PLAYLIST = 'SELECTED_PLAYLIST';
export const PLAYER_ERROR = 'PLAYER_ERROR';
export const MUSIC_PLAYER_SELECTED = 'MUSIC_PLAYER_SELECTED';
export const FETCH_PLAYLISTS_SUCCESS = 'FETCH_PLAYLISTS_SUCCESS';
export const FETCH_SONGS_SUCCESS = 'FETCH_SONGS_SUCCESS';
export const FETCH_USER_PLAYLISTS_SUCCESS = 'FETCH_USER_PLAYLISTS_SUCCESS';
export const FETCH_FB_PLAYLISTS_SUCCESS = 'FETCH_FB_PLAYLISTS_SUCCESS';
export const CREATE_NEW_PLAYLIST_CLICKED = 'CREATE_NEW_PLAYLIST_CLICKED';
export const FETCH_USER_PROFILE_SUCCESS = 'FETCH_USER_PROFILE_SUCCESS';
export const FETCH_USER_PROFILE_FAILURE = 'FETCH_USER_PROFILE_FAILURE';
export const GENERIC_MUSIC_LOADED_SUCCESS = 'GENERIC_MUSIC_LOADED_SUCCESS';
export const GENERIC_TRACK_CHANGED = 'GENERIC_TRACK_CHANGED';
export const SPOTIFY_ERROR = 'SPOTIFY_ERROR';
export const FETCH_RECOMMENDED_PLAYLISTS_SUCCESS = 'FETCH_RECOMMENDED_PLAYLISTS_SUCCESS';
export const GENERIC_MUSIC_SKIP_TO_NEXT = 'workout_music_generic_music_skip_to_next';
export const GENERIC_MUSIC_SKIP_TO_PREVIOUS = 'workout_generic_music_skip_to_previous';
export const GENERIC_MUSIC_PLAY = 'workout_generic_music_play';
export const GENERIC_MUSIC_PAUSE = 'workout_generic_music_pause';
export const PLAYER_PAUSED = 'workout_music_player_pause';
export const PLAYER_PLAY = 'workout_music_player_play';
export const PLAYER_SHUFFLE_ON = 'workout_music_player_shuffle_on';
export const PLAYER_SHUFFLE_OFF = 'workout_music_player_shuffle_off';
export const PLAYER_SKIP_FORWARD = 'workout_music_player_skip';
export const PLAYER_SKIP_BACK = 'workout_music_player_back';
export const SELECT_TRACK = 'workout_music_player_select_song';
export const CLEAN_MUSIC_STORE = 'CLEAN_MUSIC_STORE';
export const SELECT_MUSIC_SOURCE_BUTTON_PRESS = 'SELECT_MUSIC_SOURCE_BUTTON_PRESS';
export const SAME_MUSIC_SOURCE_SELECTED = 'SAME_MUSIC_SOURCE_SELECTED';
export const GENERIC_PLAYER_STATE_CHANGE = 'GENERIC_PLAYER_STATE_CHANGE';
export const CLEAN_GENERIC_MUSIC = 'CLEAN_GENERIC_MUSIC';
export const GENERIC_MUSIC_ERROR = 'GENERIC_MUSIC_ERROR';
export const CHANGE_PLAYLIST_PRESSED = 'CHANGE_PLAYLIST_PRESSED';
export const BACK_FROM_PLAYLIST_SELECT_SCREEN_PRESSED = 'BACK_FROM_PLAYLIST_SELECT_SCREEN_PRESSED';
export const BACK_FROM_MUSIC_SOURCE_SELECT_SCREEN_PRESSED = 'BACK_FROM_MUSIC_SOURCE_SELECT_SCREEN_PRESSED';
export const LOAD_SPOTIFY_ONBOARDING = 'LOAD_SPOTIFY_ONBOARDING';
export const SPOTIFY_ONBOARDING_CLOSED = 'SPOTIFY_ONBOARDING_CLOSED';

let musicPlayerSource: IMusicPlayerSource | null = null;

export interface IGenericMusicTrack {
  id: string;
  url: string;
}

export const STORAGE_MUSIC_PLAYER = 'musicPlayer';
const STORAGE_PLAYLIST_URI = 'playlistUri';
const STORAGE_PLAYLIST_TITLE = 'playlistTitle';
const STORAGE_PLAYLIST_ID = 'playlistId';

export const fetchMusicDataFromAsyncStorage = async () => {
  const musicData = await Promise.all([
    Storage.getItem(STORAGE_MUSIC_PLAYER),
    Storage.getItem(STORAGE_PLAYLIST_URI),
    Storage.getItem(STORAGE_PLAYLIST_TITLE),
    Storage.getItem(STORAGE_PLAYLIST_ID),
  ]);
  return {
    musicPLayer: musicData[0],
    playlistUri: musicData[1],
    playlistTitle: musicData[2],
    playlistId: musicData[3],
  };
};

const clearMusicDataFromAsyncStorage = async () => {
  await Promise.all([
    Storage.setItem(STORAGE_MUSIC_PLAYER, ''),
    Storage.setItem(STORAGE_PLAYLIST_URI, ''),
    Storage.setItem(STORAGE_PLAYLIST_TITLE, ''),
    Storage.setItem(STORAGE_PLAYLIST_ID, ''),
  ]);
};

const clearPlaylistDataFromAsyncStorage = async () => {
  await Promise.all([
    Storage.setItem(STORAGE_PLAYLIST_URI, ''),
    Storage.setItem(STORAGE_PLAYLIST_TITLE, ''),
    Storage.setItem(STORAGE_PLAYLIST_ID, ''),
  ]);
};

export const initialiseMusicAndStartNonGeneric = () => (
  async (dispatch: (action: any) => void, getState: () => IAppStore) => {
    try {
      dispatch({type: MUSIC_AUTOPLAY_INITIALISING});
      const state = getState();
      const {musicPlayerToAutoplay} = state.music;
      const activeMusicPlayer = getState().music.musicPlayer;
      if (activeMusicPlayer) {
        return;
      }
      if (state.homeWorkouts.onlyGenericMusicAllowed) {
        await genericMusicPlayerSelected(dispatch, getState);
        return;
      }
      if (musicPlayerToAutoplay === musicPlatform.Spotify) {
        await spotifySelected(dispatch, getState);
      } else if (musicPlayerToAutoplay === musicPlatform.AppleMusic) {
        await appleMusicSelected(dispatch, getState);
      } else {
        await genericMusicPlayerSelected(dispatch, getState);
      }

    } finally {
      dispatch({type: MUSIC_AUTOPLAY_INITIALISED});
    }
  }
);

export const resetMusicPlayer = async (dispatch: (action: any) => void) => {
  cleanUpMusic(dispatch)
    .catch((e) => Logger.notify(e));
  const {musicPLayer} = await fetchMusicDataFromAsyncStorage();
  if (!!musicPLayer) {
    dispatch({
      type: MUSIC_AUTOPLAY_SETUP,
      payload: { musicPlayer: musicPLayer },
    });
  }
  if (musicPLayer !== musicPlatform.AppleMusic && musicPLayer !== musicPlatform.Spotify) {
    dispatch(initialiseMusicAndStartNonGeneric());
  }
};

export const playlistsSelectScreenFocused = () => (
  async (dispatch: (action: any) => void, getState: () => IAppStore) => {
    if (getState().music.canFetchUserPlaylistsOnPlaylistScreenFocus) {
      await musicPlayerSource!.loadUserPlaylists();
    }
  }
);

export const genericMusicSkipToNext = () => {
  return {
    type: GENERIC_MUSIC_SKIP_TO_NEXT,
    ...analyicsForAction(GENERIC_MUSIC_SKIP_TO_NEXT),
  };
};

export const genericMusicSkipToPrevious = () => {
  return {
    type: GENERIC_MUSIC_SKIP_TO_PREVIOUS,
    ...analyicsForAction(GENERIC_MUSIC_SKIP_TO_PREVIOUS),
  };
};

export const genericMusicPause = () => {
  return {
    type: GENERIC_MUSIC_PAUSE,
    ...analyicsForAction(GENERIC_MUSIC_PAUSE),
  };
};

export const genericMusicPlay = () => {
  return {
    type: GENERIC_MUSIC_PLAY,
    ...analyicsForAction(GENERIC_MUSIC_PLAY),
  };
};

export const loadTracks = async (cardioOrResistance: CardioOrResistance, dispatch) => {
  const bookingServiceClient = new BookingServiceClient(getApiConfig(), fetch, new AuthTokenBuilder());
  const tracks = await bookingServiceClient.getGenericTrackListFor(cardioOrResistance);
  dispatch({
    type: GENERIC_MUSIC_LOADED_SUCCESS,
    payload: { cardioOrResistance, tracks },
  });
};

const genericMusicPlayerSelected = async (dispatch, getState: () => IAppStore, autostart = false) => {
  try {
    const { homeWorkoutSession } = getState().homeWorkouts;
    if (!homeWorkoutSession) {
      return;
    }
    await cleanUpMusic(dispatch);
    dispatch({type: MUSIC_PLAYER_SELECTED, payload: musicPlatform.FitbeatMusic, autostart});

    await loadTracks(homeWorkoutSession.workoutSettings.cardioOrResistance, dispatch);

    saveMusicPlayerToAutoLoadInBackground(dispatch, musicPlatform.FitbeatMusic);
  } catch (e) {
    dispatch({type: MUSIC_PLAYER_SELECTED, payload: null});
    stopGenericMusic(dispatch)
      .catch((e) => Logger.notify(e));
    dispatch(onError(e, 'Load generic playlist'));
  }
};

const stopGenericMusic = async (dispatch) => {
  dispatch({
    type: CLEAN_GENERIC_MUSIC,
  });
};

export const onError = (error: Error, caller = ''): IPlayerError => {
  Logger.notify(error, (report) => {
    report.errorClass = 'Music Player';
    report.errorMessage = `Caller: ${caller}\n\nMessage: ${error.message}`;
  });
  return {
    type: PLAYER_ERROR,
    payload: error,
  };
};

export const playerStateChange = (playerState: IPlayerState) => {
  return {
    type: PLAYER_STATE_CHANGE,
    payload: { playerState },
  };
};

export const connectionStateChange = (isConnected: boolean) => {
  return {
    type: CONNECTION_STATE_CHANGE,
    payload: { isConnected },
  };
};

export const pausePlayback = () => (
  async (dispatch: (action: any) => void) => {
    try {
      await musicPlayerSource!.pause();
      dispatch({
        type: PLAYER_PAUSED,
        ...analyicsForAction(PLAYER_PAUSED),
      });
    } catch (e) {
      spotifyError(dispatch, e);
    }
  });

export const resumePlayback = () => (
  async (dispatch: (action: any) => void) => {
    try {
      await musicPlayerSource!.play();
      dispatch({
        type: PLAYER_PLAY,
        ...analyicsForAction(PLAYER_PLAY),
      });
    } catch (e) {
      spotifyError(dispatch, e);
    }
  });

export const shuffleOn = () => (
  async (dispatch: (action: any) => void) => {
    try {
      await musicPlayerSource!.setShuffling(true);
      dispatch({
        type: PLAYER_SHUFFLE_ON,
        ...analyicsForAction(PLAYER_SHUFFLE_ON),
      });
    } catch (e) {
      spotifyError(dispatch, e);
    }
  });

export const shuffleOff = () => (
  async (dispatch: (action: any) => void) => {
    try {
      await musicPlayerSource!.setShuffling(false);
      dispatch({
        type: PLAYER_SHUFFLE_OFF,
        ...analyicsForAction(PLAYER_SHUFFLE_OFF),
      });
    } catch (e) {
      spotifyError(dispatch, e);
    }
  });

export const skipForward = () => (
  async (dispatch: (action: any) => void) => {
    try {
      await musicPlayerSource!.skipNext();
      dispatch({
        type: PLAYER_SKIP_FORWARD,
        ...analyicsForAction(PLAYER_SKIP_FORWARD),
      });
    } catch (e) {
      spotifyError(dispatch, e);
    }
  });

export const skipBackward = () => (
  async (dispatch: (action: any) => void, getState: () => IAppStore) => {
    try {
      await musicPlayerSource!.skipPrevious();
      dispatch({
        type: PLAYER_SKIP_BACK,
        ...analyicsForAction(PLAYER_SKIP_BACK),
      });
    } catch (e) {
      spotifyError(dispatch, e);
    }
  });

export const selectTrack = (song: ISong) => (
  async (dispatch: (action: any) => void) => {
    try {
      await musicPlayerSource!.playSong(song);
      dispatch({
        type: SELECT_TRACK,
        ...analyicsForAction(SELECT_TRACK),
      });
    } catch (e) {
      spotifyError(dispatch, e);
    }
  });

export const cleanUpMusic = async (dispatch) => {
  dispatch({ type: CLEAN_MUSIC_STORE });
  try {
    if (musicPlayerSource) {
      await musicPlayerSource.clean();
      musicPlayerSource = null;
    }
  } catch (e) { /* ignore failures */
  }

  try {
    await stopGenericMusic(dispatch);
  } catch (e) {
    // ignore
  }
};

const spotifySelected = async (dispatch: (action: any) => void, getState: () => IAppStore) => {
  try {
    await cleanUpMusic(dispatch);
    dispatch({type: MUSIC_PLAYER_SELECTED, payload: musicPlatform.Spotify,
      ...analyicsForAction('workout_music_players_spotify_selected'),
    });
    musicPlayerSource = new SpotifyMusicSource(
      dispatch,
      getState,
      (playerState) => dispatch(playerStateChange(playerState)));
    await musicPlayerSource.initialize();
    saveMusicPlayerToAutoLoadInBackground(dispatch, musicPlatform.Spotify);
  } catch (e) {
    Logger.leaveBreadcrumb('Spotify connection failed', { error: e});
    dispatch({
      type: FETCH_USER_PROFILE_FAILURE,
      payload: {
        authenticationFailMessage:
          'Failed to connect to Spotify. Please stop Spotify on other devices, restart the Spotify app and try again.' +
            (__DEV__ ? ` debug: ${e.message}` : ''),
        authenticationFailedPlayer: musicPlatform.Spotify,
      },
      ...analyicsForAction('workout_music_players_connection_fail'),
    });

    dispatch(onError(e, 'spotify selected'));
  }
};

export const startMusicOnSpotify =  () => {
  return async (dispatch: (action: any) => void, getState: () => IAppStore) => {
    await spotifySelected(dispatch, getState);
  };
};

export const switchSpotifyAccount = () => {
  return async (dispatch: (action: any) => void) => {
    try {
      await cleanUpMusic(dispatch);
      const didClearSuccessfully = await EliteAppManagement.clearSpotifyAppData();
      if (!didClearSuccessfully) {
        throw new Error('Failed to clear spotify app data')
      }
      launchOnBoarding(dispatch);
    } catch (e) {
      dispatch(onError(e, 'spotify account switched'))
    }
  }
}

export const saveMusicPlayerToAutoLoad = async (dispatch: (action: any) => void, musicPlayer: musicPlatform) => {
  await Storage.setItem('musicPlayer', musicPlayer);
  dispatch({type: SET_MUSIC_PLAYER_TO_AUTOPLAY, payload: { musicPlayer }});
};

const saveMusicPlayerToAutoLoadInBackground = (dispatch: (action: any) => void, musicPlayer: musicPlatform) => {
  saveMusicPlayerToAutoLoad(dispatch, musicPlayer)
    .catch((e) => Logger.notify(e));
};

async function appleMusicSelected(dispatch: (action: any) => void, getState: () => IAppStore) {
  try {
    await cleanUpMusic(dispatch);
    dispatch({ type: MUSIC_PLAYER_SELECTED, payload: musicPlatform.AppleMusic,
      ...analyicsForAction('workout_music_players_apple_selected'),
    });
    musicPlayerSource = new AppleMusicSource(
      dispatch,
      getState,
      (playerState) => dispatch(playerStateChange(playerState)));
    await musicPlayerSource.initialize();
    saveMusicPlayerToAutoLoadInBackground(dispatch, musicPlatform.AppleMusic);
  } catch (e) {
    const enableFakeAppleMusicSuccessForTesting = false;
    if (enableFakeAppleMusicSuccessForTesting) {
      saveMusicPlayerToAutoLoadInBackground(dispatch, musicPlatform.AppleMusic);
    } else {
      Logger.leaveBreadcrumb('Applemusic connection failed', { error: e });
      if (e.message.includes('User is not an apple music subscriber. Status Code: 422')) {
        dispatch({
          type: FETCH_USER_PROFILE_FAILURE,
          payload: {
            authenticationFailMessage:
              'You need to be an Apple Music subscriber.',
            authenticationFailedPlayer: musicPlatform.AppleMusic,
          },
          ...analyicsForAction('workout_music_players_connection_fail'),
        });
        Logger.leaveBreadcrumb(e);
      } else if (e.message.includes('User rejected permissions to access apple music. Status Code: 421')) {
        dispatch({
          type: FETCH_USER_PROFILE_FAILURE,
          payload: {
            authenticationFailMessage:
              'Please give Fitbeat permissions to access Apple Music in your device settings.',
            authenticationFailedPlayer: musicPlatform.AppleMusic,
          },
          ...analyicsForAction('workout_music_players_connection_fail'),
        });
        Logger.leaveBreadcrumb(e);
      } else {
        dispatch({
          type: FETCH_USER_PROFILE_FAILURE,
          payload: {
            authenticationFailMessage:
              'Apple Music failed to connect. Please check your Apple Music app and login.',
            authenticationFailedPlayer: musicPlatform.AppleMusic,
          },
          ...analyicsForAction('workout_music_players_connection_fail'),
        });
      }
      dispatch(onError(e));
    }
  }
}

export const selectMusicSourceButtonPress = (): ISelectMusicSourceButtonPress => {
  return {
    type: SELECT_MUSIC_SOURCE_BUTTON_PRESS,
  };
};

export const sameMusicSourceSelected = () => {
  return {
    type: SAME_MUSIC_SOURCE_SELECTED,
  };
};

export const changePlaylistPressed = () => {
  return {
    type: CHANGE_PLAYLIST_PRESSED,
    ...analyicsForAction('workout_music_player_back_to_playlists'),
  };
};

export const backFromPlaylistSelectScreenPressed = () => {
  return {
    type: BACK_FROM_PLAYLIST_SELECT_SCREEN_PRESSED,
    ...analyicsForAction('workout_music_playlist_select_back'),
  };
};

export const playlistSelected = (item: IPlaylist) => (
  async (dispatch: (action: any) => void, getReduxState: () => IAppStore) => {
    const currentState = getReduxState();
    const isFbDefaultPlaylist = currentState.music.defaultBuildBurnPlaylistIds.some(
      (playlistId) => item.id === playlistId);
    if (isFbDefaultPlaylist) {
      await clearPlaylistDataFromAsyncStorage();
    } else {
      await Promise.all([
        Storage.setItem(STORAGE_PLAYLIST_URI, item.uri),
        Storage.setItem(STORAGE_PLAYLIST_ID, item.id),
        Storage.setItem(STORAGE_PLAYLIST_TITLE, item.title)]);
    }
    try {
      dispatch({
        type: SELECTED_PLAYLIST,
        payload: { playlist: item },
        ...analyicsForAction('workout_music_playlists_select'),
      });
      const playerState = await musicPlayerSource!.playPlaylist(item);
      dispatch({
        type: PLAYER_STATE_CHANGE,
        payload: { playerState },
      });
    } catch (e) {
      dispatch(onError(e, 'playlist selected'));
    }
  }
);

export const spotifyTilePressed = () => {
  return (dispatch: (action: any) => void, getState: () => IAppStore) => {
    const currentMusicPlayer = getState().music.musicPlayer;
    if (currentMusicPlayer ===  musicPlatform.Spotify) {
      return dispatch(sameMusicSourceSelected());
    }
    if (currentMusicPlayer === musicPlatform.AppleMusic) {
      dispatch(pausePlayback())
    }
    if (currentMusicPlayer === musicPlatform.FitbeatMusic) {
      dispatch(genericMusicPause())
    }
    launchOnBoarding(dispatch);
  }
}

export const musicSourceSelected = (source: musicPlatform) => (
  async (dispatch: (action: any) => void, getState: () => IAppStore) => {
      const currentMusicPlayer = getState().music.musicPlayer;
      if (currentMusicPlayer === source) {
        return dispatch(sameMusicSourceSelected());
      }
      if (source === musicPlatform.FitbeatMusic) {
        const autoStart = true;
        await genericMusicPlayerSelected(dispatch, getState, autoStart);
      }

      if (source === musicPlatform.Spotify) {
        await spotifySelected(dispatch, getState);
      }
      if (source === musicPlatform.AppleMusic) {
        if (Platform.OS === 'ios') {
          return appleMusicSelected(dispatch, getState);
        }
      }
  }
);

export const spotifyOnBoardingClosed = () => {
  return (dispatch: (action: any) => void, getState: () => IAppStore) => {
    const musicPlayer = getState().music.musicPlayer;
    dispatch({type: SPOTIFY_ONBOARDING_CLOSED});
    if (musicPlayer === musicPlatform.AppleMusic) {
      dispatch(resumePlayback());
    }
    if (musicPlayer === musicPlatform.FitbeatMusic) {
      dispatch(genericMusicPlay())
    }
  };
};

export const launchOnBoarding = (dispatch: any) => {
  dispatch({type: LOAD_SPOTIFY_ONBOARDING});
};

export const createNewPlaylistClicked = () => (
  async (dispatch: (action: any) => void, getState: () => IAppStore) => {
    try {
      const musicPlayer = getState().music.musicPlayer;
      if (musicPlayer === musicPlatform.Spotify) {
        await openLink('spotify://');
      } else if (musicPlayer === musicPlatform.AppleMusic) {
        await openLink('music://');
      }
      dispatch({ type: CREATE_NEW_PLAYLIST_CLICKED });
    } catch (e) {
      dispatch(onError(e, 'create new playlist clicked'));
    }
  }
);

export const genericMusicError = (error: string) => {
  return async (dispatch: (action: any) => void) => {
    Logger.notify(error);

    setTimeout(() => {
      dispatch({
        type: GENERIC_MUSIC_ERROR,
        payload: {
          error,
        },
      });
    }, 2000);
  }
};

const spotifyError = (
  dispatch: any,
  e: ErrorEvent) => {
  Logger.leaveBreadcrumb('Error while using spotify remote', e);
  dispatch({
    type: SPOTIFY_ERROR,
  });
  dispatch(openSetupScreen(DeviceSetupScreens.Music));
};

export const backFromMusicSourceSelectScreenPressed = () => (
  async (dispatch: (action: any) => void, getState: () => IAppStore) => {
    const state = getState();

    if (state.music.authenticationFailed || state.music.musicInitialiseState !== 'done') {
      dispatch(closeSetupScreen());
    }

    dispatch({
      type: BACK_FROM_MUSIC_SOURCE_SELECT_SCREEN_PRESSED,
      ...analyicsForAction('workout_music_source_select_back'),
    });
  });
interface ISetMusicPlayerToAutoPlay {
  type: typeof SET_MUSIC_PLAYER_TO_AUTOPLAY;
  payload: {
    musicPlayer: musicPlatform,
  };
}

interface ISelectedTrack {
  type: typeof SELECT_TRACK;
  payload: {
    playerState: IPlayerState,
  };
}

interface IPlayerSkipBack {
  type: typeof PLAYER_SKIP_BACK;
  payload: {
    playerState: IPlayerState,
  };
}

interface IPlayerSkipForward {
  type: typeof PLAYER_SKIP_FORWARD;
  payload: {
    playerState: IPlayerState,
  };
}

interface IPlayerShuffleOff {
  type: typeof PLAYER_SHUFFLE_OFF;
  payload: {
    playerState: IPlayerState,
  };
}

interface IPlayerShuffleOn {
  type: typeof PLAYER_SHUFFLE_ON;
  payload: {
    playerState: IPlayerState,
  };
}

interface IPlayerPlay {
  type: typeof PLAYER_PLAY;
  payload: {
    playerState: IPlayerState,
  };
}

interface IPlayerPaused {
  type: typeof PLAYER_PAUSED;
  payload: {
    playerState: IPlayerState,
  };
}

interface IPlayerStateChange {
  type: typeof PLAYER_STATE_CHANGE;
  payload: {
    playerState: IPlayerState,
  };
}

interface IConnectionStateChange {
  type: typeof CONNECTION_STATE_CHANGE;
  payload: {
    isConnected: boolean,
    playerState: IPlayerState | null,
  };
}

interface ISelectedPlaylist {
  type: typeof SELECTED_PLAYLIST;
  payload: {
    playlist: IPlaylist | null;
  };
}

interface IFetchPlaylistSuccess {
  type: typeof FETCH_PLAYLISTS_SUCCESS;
  payload: {
    fitbeatPlaylists: IPlaylist[];
    userPlaylists: IPlaylist[];
  };
}

interface IMusicPlayerSelected {
  type: typeof MUSIC_PLAYER_SELECTED;
  payload: musicPlatform | null;
  autostart?: boolean;
}

export interface IFetchSongsSuccess {
  type: typeof FETCH_SONGS_SUCCESS;
  payload: ISong[];
}

export interface IFetchUserPlaylistSuccess {
  type: typeof FETCH_USER_PLAYLISTS_SUCCESS;
  payload: any;
}

export interface IFetchFBPlaylistSuccess {
  type: typeof FETCH_FB_PLAYLISTS_SUCCESS;
  payload: {
    playlists: IPlaylist[];
    defaultBuildBurnPlaylistIds: string[];
  };
}

export interface IFetchRecommendedPlaylistSuccess {
  type: typeof FETCH_RECOMMENDED_PLAYLISTS_SUCCESS;
  payload: IPlaylist[];
}

interface ICreateNewPlaylistClicked {
  type: typeof CREATE_NEW_PLAYLIST_CLICKED;
}

interface IPlayerError {
  type: typeof PLAYER_ERROR;
  payload: Error;
}

export interface IFetchUserProfileSuccess {
  type: typeof FETCH_USER_PROFILE_SUCCESS;
  payload: {
    product: string;
    displayName: string;
  };
}

interface IFetchUserProfileFailure {
  type: typeof FETCH_USER_PROFILE_FAILURE;
  payload?: {
    authenticationFailMessage: string,
    authenticationFailedPlayer: musicPlatform,
  };
}

interface IGenericMusicLoadedSuccess {
  type: typeof GENERIC_MUSIC_LOADED_SUCCESS;
  payload: {
    cardioOrResistance: CardioOrResistance,
    tracks: IGenericMusicTrack[],
  };
}

interface IGenericMusicTrackChanged {
  type: typeof GENERIC_TRACK_CHANGED;
  payload: {
    currentGenericPlayingTrackNumber: number;
  };
}

interface ISpotifyError {
  type: typeof SPOTIFY_ERROR;
  payload: {
    authenticationFailMessage: string,
  };
}

interface ICleanMusicStore {
  type: typeof CLEAN_MUSIC_STORE;
}

interface ISelectMusicSourceButtonPress {
  type: typeof SELECT_MUSIC_SOURCE_BUTTON_PRESS;
}

interface ISameMusicSourceSelected {
  type: typeof SAME_MUSIC_SOURCE_SELECTED;
}

interface IGenericMusicIsSkipping {
  type: typeof GENERIC_MUSIC_SKIP_TO_NEXT | typeof GENERIC_MUSIC_SKIP_TO_PREVIOUS;
}

interface IGenericMusicPaused {
  type: typeof GENERIC_MUSIC_PAUSE;
}

interface IGenricMusicPlay {
  type: typeof GENERIC_MUSIC_PLAY;
}

interface IGenericMusicClean {
  type: typeof CLEAN_GENERIC_MUSIC;
}
interface IMusicInitialisingAutoplay {
  type: typeof MUSIC_AUTOPLAY_INITIALISING;
}
interface IMusicInitialisedAutoplay {
  type: typeof MUSIC_AUTOPLAY_INITIALISED;
}
interface IMusicSetupAutoPlay {
  type: typeof MUSIC_AUTOPLAY_SETUP;
  payload: {
    musicPlayer: musicPlatform,
  };
}
interface IGenericMusicError {
  type: typeof GENERIC_MUSIC_ERROR;
  payload: {
    error: string;
  };
}

interface IChoosePlaylistPressed {
  type: typeof CHANGE_PLAYLIST_PRESSED;
}

interface ILoadSpotifyOnBoarding {
  type: typeof LOAD_SPOTIFY_ONBOARDING;
}

interface ISpotifyOnBoardingClosed {
  type: typeof SPOTIFY_ONBOARDING_CLOSED;
}
interface IBackFromPlaylistSelectScreenPressed {
  type: typeof BACK_FROM_PLAYLIST_SELECT_SCREEN_PRESSED;
}

interface IBackFromMusicSourceSelectScreenPressed {
  type: typeof BACK_FROM_MUSIC_SOURCE_SELECT_SCREEN_PRESSED;
}

export type MusicActionTypes =
  IOpenHomeWorkoutRequest |
  ISetMusicPlayerToAutoPlay |
  IMusicSetupAutoPlay |
  IMusicInitialisingAutoplay |
  IMusicInitialisedAutoplay |
  ISelectedTrack |
  IPlayerSkipBack |
  IPlayerSkipForward |
  IPlayerShuffleOn |
  IPlayerShuffleOff |
  IPlayerPlay |
  IPlayerPaused |
  IPlayerStateChange |
  IConnectionStateChange |
  ISelectedPlaylist |
  IPlayerError |
  IFetchPlaylistSuccess |
  IMusicPlayerSelected |
  IFetchSongsSuccess |
  IFetchUserPlaylistSuccess |
  IFetchFBPlaylistSuccess |
  ICreateNewPlaylistClicked |
  IFetchUserProfileSuccess |
  IFetchUserProfileFailure |
  IGenericMusicLoadedSuccess |
  IGenericMusicTrackChanged |
  IFetchRecommendedPlaylistSuccess |
  ICleanMusicStore |
  ISpotifyError |
  ISelectMusicSourceButtonPress |
  ISameMusicSourceSelected |
  IGenericMusicIsSkipping |
  IGenericMusicPaused |
  IGenricMusicPlay |
  IGenericMusicClean |
  IGenericMusicError |
  IChoosePlaylistPressed |
  IBackFromPlaylistSelectScreenPressed |
  IBackFromMusicSourceSelectScreenPressed |
  ILoadSpotifyOnBoarding |
  ISpotifyOnBoardingClosed;
